'use strict';

/**
 * SuperDeveloperSettingsController
 *
 * Generic Super-admin "Developer Settings" page (WEB-3546). A super user (gated by the
 * SS_DEVELOPER_SETTINGS_VIEW / SS_DEVELOPER_SETTINGS_UPDATE permissions) selects a developer
 * and toggles per-developer configuration. Each section is persisted as its own row in the
 * `developer_settings` key/value store via the `sec/developer/settings` CRUD API.
 *
 * Sections:
 *   1. Finance Report Settings — "Booking Fee billed/received date aligned to OTP date"
 *      (name = finance_reports_booking_fee_dates_on_otp_date, value = "1"|"0"). When ON, finance
 *      reports treat the Booking Fee's billing date AND its instrument receiving date as the
 *      sale's OTP date, so both the billed and collected booking-fee amounts fall in the OTP's
 *      reporting period.
 *   2. Notifications — per-developer email notification toggles
 *      (name = developer_email_notifications, value = comma-separated notification ids).
 *      Moved here from the former standalone Developer Notifications page.
 *   3. Security (WEB-3553) — "Enforce 2FA at login for all users". Unlike sections 1-2 this is
 *      NOT a developer_settings row: the flag is `is_twofa_enforced` on the developer itself,
 *      read from GET sec/super/developers (iteration 2 — the `developers/` list payload does
 *      not carry the column) and written via POST sec/super/developers/{id}/set-2fa-enforcement.
 *      Gated by SS_ORG_2FA_ENFORCEMENT, independent of the SS_DEVELOPER_SETTINGS_* permissions
 *      that gate sections 1-2.
 *
 * Per-section view/edit mode: a section is read-only by default (Edit button). Clicking Edit
 * makes only that section editable (Cancel + Save). Save persists just that section then
 * returns it to read-only; Cancel reverts that section to its last-loaded values. A save/refresh
 * of one section never clobbers another section that is mid-edit.
 *
 * The developer dropdown is a searchable jQuery Nice Select widget (class "dashboard-select",
 * initialised in the blade); it is rebuilt via niceSelect('update') once the async developer
 * list resolves so the late-populated ng-options render.
 */
class SuperDeveloperSettingsController {
    /**
     * @param {Object}   SSUtilService
     * @param {Object}   SSUserService  - permission/role helper (isSuper, hasPermission)
     * @param {Object}   SSHttpService  - API request helper
     * @param {Object}   OTPService     - 2FA gate for sensitive saves
     * @param {Object}   SSAlertService - user-facing alerts
     * @param {Function} $timeout       - Angular $timeout (defer nice-select refresh past digest)
     */
    constructor(SSUtilService, SSUserService, SSHttpService, OTPService, SSAlertService, $timeout) {
        const self = this;

        self.SSUtilService = SSUtilService;
        self.SSUserService = SSUserService;
        self.SSHttpService = SSHttpService;
        self.OTPService = OTPService;
        self.SSAlertService = SSAlertService;
        self.$timeout = $timeout;

        self.data = SHOWSUITE.data;
        self.SETTING_BF_DATES_ON_OTP_DATE = SHOWSUITE.DEVELOPER_SETTINGS_FINANCE_REPORT_BF_DATES_ON_OTP_DATE;
        self.SETTING_EMAIL_NOTIFICATIONS = 'developer_email_notifications';

        self.developers = null;
        self.selectedDeveloper = null;
        self.developerSettings = null;

        // Section 1 — Finance Report Settings: Booking Fee billed/received date aligned to OTP date
        self.bfSetting = null;          // the developer_settings row for this flag (carries id on update)
        self.bfDatesOnOtpDate = false;  // toggle model
        self.financeEditMode = false;   // false = read-only, true = editing
        self._bfSnapshot = false;       // value captured on entering edit (for Cancel)

        // Section 2 — Notifications (moved from the standalone Developer Notifications page)
        self.notificationSetting = null;  // the developer_settings row for developer_email_notifications
        self.notificationEditMode = false;
        self._notifSnapshot = [];          // checks captured on entering edit (for Cancel)
        self.const_dict_notifications = [
            {id: SHOWSUITE.EMAIL_NOTIFICATION_SPA_DELIVERY_EXPIRY, value: "Email Notification SPA Delivery Expiry", check: false},
            {id: SHOWSUITE.EMAIL_NOTIFICATION_SPA_EXPIRY, value: "Email Notification SPA Expiry", check: false},
            {id: SHOWSUITE.EMAIL_NOTIFICATION_OPTION_EXERCISED_SUMMARY, value: "Email Notification Option Exercised Summary", check: false},
            {id: SHOWSUITE.EMAIL_NOTIFICATION_OPTION_EXERCISED, value: "Email Notification Option Exercised", check: false},
            {id: SHOWSUITE.EMAIL_NOTIFICATION_OUTSTANDING_PROGRESSIVE_PAYMENTS, value: "Email Notification Outstanding Progressive Payments", check: false}
        ];

        // Section 3 — Security (WEB-3553): org-level enforced 2FA at login
        self.twofaEnforced = false;       // toggle model, mirrors developer.is_twofa_enforced
        self.securityEditMode = false;    // false = read-only, true = editing
        self._securitySnapshot = false;   // value captured on entering edit (for Cancel)
        self._twofaEnforcedByDeveloperId = {};  // id -> 0|1, from GET sec/super/developers

        if (self.SSUserService.isSuper()) {
            self.init();
        }
    }

    init() {
        const self = this;
        self.getDevelopers();
        self.getDeveloperSecurityFlags();
    }

    /**
     * Whether the current user may edit (enter edit mode + save) settings on this page.
     * @returns {boolean}
     */
    canUpdate() {
        return this.SSUserService.hasPermission('SS_DEVELOPER_SETTINGS_UPDATE');
    }

    /**
     * Whether the current user may view the developer_settings sections (Finance/Notifications).
     * @returns {boolean}
     */
    canViewSettings() {
        return this.SSUserService.hasPermission('SS_DEVELOPER_SETTINGS_VIEW');
    }

    /**
     * Whether the current user may manage org-level 2FA enforcement (Security section).
     * @returns {boolean}
     */
    canManageOrg2FA() {
        return this.SSUserService.hasPermission('SS_ORG_2FA_ENFORCEMENT');
    }

    /**
     * Load the developer dropdown options, then rebuild the searchable nice-select widget.
     */
    getDevelopers() {
        let self = this;

        loading(true);
        self.SSHttpService.getAPIRequest('developers/', {limit: 1000}).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            self.developers = response.data.developers;
            // ng-options populate after this digest; rebuild the nice-select so the options render
            self.$timeout(function () {
                jQuery('#developer-select').niceSelect('update');
            });
        });
    }

    /**
     * ng-change handler for the developer dropdown. Resets every section to read-only, hydrates
     * the Security section from the already-loaded developers list and loads the newly selected
     * developer's settings rows.
     */
    developerSelected() {
        const self = this;
        self.financeEditMode = false;
        self.notificationEditMode = false;
        self.securityEditMode = false;
        self.parseTwoFAEnforcement();
        self.getDeveloperSettings();
    }

    /**
     * Load the per-developer org-security flags from the canonical super list endpoint
     * (WEB-3553 iteration 2 — the `developers/` list payload does not carry the column).
     * Skipped for users without SS_ORG_2FA_ENFORCEMENT: the endpoint requires it and the
     * Security section is hidden for them anyway.
     */
    getDeveloperSecurityFlags() {
        const self = this;

        if (!self.canManageOrg2FA()) {
            return;
        }

        self.SSHttpService.getAPIRequest('sec/super/developers').then(function (response) {
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            self._twofaEnforcedByDeveloperId = {};
            (response.data.developers || []).forEach(function (developer) {
                self._twofaEnforcedByDeveloperId[developer.id] = parseInt(developer.is_twofa_enforced || 0, 10);
            });
            // re-hydrate in case a developer was selected before the flags resolved
            if (self.selectedDeveloper && !self.securityEditMode) {
                self.parseTwoFAEnforcement();
            }
        });
    }

    /**
     * Hydrate the Security section's toggle from the selected developer's `is_twofa_enforced`
     * flag (from the already-loaded sec/super/developers map — no extra request needed).
     */
    parseTwoFAEnforcement() {
        const self = this;
        self.twofaEnforced = !!parseInt(self._twofaEnforcedByDeveloperId[self.selectedDeveloper] || 0, 10);
    }

    /**
     * Fetch the selected developer's settings and hydrate every section. Skipped for users who
     * hold only SS_ORG_2FA_ENFORCEMENT — the developer_settings API requires the
     * SS_DEVELOPER_SETTINGS_VIEW permission and its sections are hidden for them anyway.
     */
    getDeveloperSettings() {
        let self = this;

        if (!self.selectedDeveloper || !self.canViewSettings()) {
            return;
        }

        loading(true);
        self.SSHttpService.getAPIRequest('sec/developer/settings/', {developer_id: self.selectedDeveloper}).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            self.developerSettings = response.data.developer_settings;
            self.parseDeveloperSettings(self.developerSettings);
        });
    }

    /**
     * Map the loaded developer_settings rows onto each section's models. A section currently in
     * edit mode is left untouched so an in-progress edit (or a refresh after saving a different
     * section) does not discard unsaved changes. Sections with no row default to OFF / empty.
     *
     * @param {Array} developerSettings - rows of {id, developer_id, name, value}
     */
    parseDeveloperSettings(developerSettings) {
        const self = this;
        developerSettings = developerSettings || [];

        let bfRow = null;
        let notifRow = null;
        developerSettings.forEach(function (element) {
            if (element.name === self.SETTING_BF_DATES_ON_OTP_DATE) {
                bfRow = element;
            } else if (element.name === self.SETTING_EMAIL_NOTIFICATIONS) {
                notifRow = element;
            }
        });

        // Finance Report Settings
        if (!self.financeEditMode) {
            if (bfRow) {
                self.bfSetting = angular.copy(bfRow);
                self.bfDatesOnOtpDate = (bfRow.value === "1" || bfRow.value === 1 || bfRow.value === true);
            } else {
                self.bfSetting = {developer_id: self.selectedDeveloper, name: self.SETTING_BF_DATES_ON_OTP_DATE, value: "0"};
                self.bfDatesOnOtpDate = false;
            }
        }

        // Notifications
        if (!self.notificationEditMode) {
            if (notifRow) {
                self.notificationSetting = angular.copy(notifRow);
                let checked = (self.notificationSetting.value || "").split(',');
                self.const_dict_notifications.forEach(function (n) {
                    n.check = (jQuery.inArray(n.id.toString(), checked) !== -1);
                });
            } else {
                self.notificationSetting = {developer_id: self.selectedDeveloper, name: self.SETTING_EMAIL_NOTIFICATIONS, value: ""};
                self.const_dict_notifications.forEach(function (n) {
                    n.check = false;
                });
            }
        }
    }

    // ---- Section 1: Finance Report Settings ----

    /** Enter edit mode for the Finance section (snapshots current value for Cancel). */
    enableFinanceEdit() {
        const self = this;
        if (!self.canUpdate()) {
            return;
        }
        self._bfSnapshot = self.bfDatesOnOtpDate;
        self.financeEditMode = true;
    }

    /** Discard unsaved Finance changes and return to read-only. */
    cancelFinanceEdit() {
        const self = this;
        self.bfDatesOnOtpDate = self._bfSnapshot;
        self.financeEditMode = false;
    }

    /** Persist the Finance section. Gated by SS_DEVELOPER_SETTINGS_UPDATE + 2FA. */
    saveFinanceSettings() {
        const self = this;
        if (!self.selectedDeveloper || !self.canUpdate() || !self.financeEditMode) {
            return;
        }
        self.OTPService.getVerified(self.OTPService.TYPE_SESSION_2FA, function () {
            self._saveFinanceSettings();
        });
    }

    _saveFinanceSettings() {
        const self = this;
        self.bfSetting.developer_id = self.selectedDeveloper;
        self.bfSetting.name = self.SETTING_BF_DATES_ON_OTP_DATE;
        self.bfSetting.value = self.bfDatesOnOtpDate ? "1" : "0";
        self._persistSetting(self.bfSetting, 'Finance report settings have been saved successfully.', function () {
            self.financeEditMode = false;
        });
    }

    // ---- Section 2: Notifications ----

    /** Enter edit mode for the Notifications section (snapshots current checks for Cancel). */
    enableNotificationEdit() {
        const self = this;
        if (!self.canUpdate()) {
            return;
        }
        self._notifSnapshot = self.const_dict_notifications.map(function (n) {
            return n.check;
        });
        self.notificationEditMode = true;
    }

    /** Discard unsaved Notifications changes and return to read-only. */
    cancelNotificationEdit() {
        const self = this;
        self.const_dict_notifications.forEach(function (n, i) {
            n.check = self._notifSnapshot[i];
        });
        self.notificationEditMode = false;
    }

    /** Persist the Notifications section. Gated by SS_DEVELOPER_SETTINGS_UPDATE + 2FA. */
    saveNotificationSettings() {
        const self = this;
        if (!self.selectedDeveloper || !self.canUpdate() || !self.notificationEditMode) {
            return;
        }
        self.OTPService.getVerified(self.OTPService.TYPE_SESSION_2FA, function () {
            self._saveNotificationSettings();
        });
    }

    _saveNotificationSettings() {
        const self = this;
        self.notificationSetting.developer_id = self.selectedDeveloper;
        self.notificationSetting.name = self.SETTING_EMAIL_NOTIFICATIONS;
        self.notificationSetting.value = self.const_dict_notifications
            .filter(function (n) { return n.check; })
            .map(function (n) { return n.id; })
            .join(',');
        self._persistSetting(self.notificationSetting, 'Notification settings have been saved successfully.', function () {
            self.notificationEditMode = false;
        });
    }

    // ---- Section 3: Security (WEB-3553) ----

    /** Enter edit mode for the Security section (snapshots current value for Cancel). */
    enableSecurityEdit() {
        const self = this;
        if (!self.canManageOrg2FA()) {
            return;
        }
        self._securitySnapshot = self.twofaEnforced;
        self.securityEditMode = true;
    }

    /** Discard the unsaved Security change and return to read-only. */
    cancelSecurityEdit() {
        const self = this;
        self.twofaEnforced = self._securitySnapshot;
        self.securityEditMode = false;
    }

    /** Persist the Security section. Gated by SS_ORG_2FA_ENFORCEMENT + 2FA. */
    saveSecuritySettings() {
        const self = this;
        if (!self.selectedDeveloper || !self.canManageOrg2FA() || !self.securityEditMode) {
            return;
        }
        self.OTPService.getVerified(self.OTPService.TYPE_SESSION_2FA, function () {
            self._saveSecuritySettings();
        });
    }

    /**
     * POST the enforcement flag for the selected developer, then sync the local models
     * (toggle + cached flag map) from the server-confirmed value.
     */
    _saveSecuritySettings() {
        const self = this;
        let url = 'sec/super/developers/' + self.selectedDeveloper + '/set-2fa-enforcement';

        loading(true);
        self.SSHttpService.postAPIRequest(url, {enabled: !!self.twofaEnforced}).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            let enforced = !!(response.data && parseInt(response.data.is_twofa_enforced || 0, 10));
            self.twofaEnforced = enforced;
            self._twofaEnforcedByDeveloperId[self.selectedDeveloper] = enforced ? 1 : 0;

            self.securityEditMode = false;
            self.SSAlertService.success('Security settings have been saved successfully.');
        });
    }

    // ---- Shared persistence ----

    /**
     * Create (POST) or update (PUT) a single developer_settings row, then refresh.
     *
     * @param {Object}   setting    - {developer_id, name, value, id?}
     * @param {string}   successMsg - alert shown on success
     * @param {Function} onSuccess  - invoked before the refresh (e.g. exit the section's edit mode)
     */
    _persistSetting(setting, successMsg, onSuccess) {
        const self = this;
        let url = 'sec/developer/settings/';

        let handleResponse = function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            if (onSuccess) {
                onSuccess();
            }
            // re-fetch so a freshly created row picks up its id for subsequent PUT updates
            self.getDeveloperSettings();
            self.SSAlertService.success(successMsg);
        };

        loading(true);
        if (setting.id) {
            self.SSHttpService.putAPIRequest(url + setting.id, setting).then(handleResponse);
        } else {
            self.SSHttpService.postAPIRequest(url, setting).then(handleResponse);
        }
    }

}

SuperDeveloperSettingsController.$inject = ['SSUtilService', 'SSUserService', 'SSHttpService', 'OTPService', 'SSAlertService', '$timeout'];
app.controller('SuperDeveloperSettingsController', SuperDeveloperSettingsController);
